import { Outlet, useLocation, useNavigate, type RouteObject } from "react-router-dom";
import { useEffect } from "react";
import type { ComponentType } from "react";
import type { RouteRecord } from "vite-react-ssg";
import { RegionProvider } from "@/i18n";
import { SeoHead } from "@/components/seo/SeoHead";
import App from "@/App";
import { REGION_CODES } from "@/router/routeManifest";

const RootRedirect = () => {
  const navigate = useNavigate();

  useEffect(() => {
    navigate("/de/", { replace: true });
  }, [navigate]);

  return null;
};

const UnprefixedRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname.replace(/^\/+/, "");
    const firstSegment = path.split("/")[0];
    if (REGION_CODES.includes(firstSegment as (typeof REGION_CODES)[number])) {
      return;
    }
    navigate(`/de/${path}${location.search}${location.hash}`, { replace: true });
  }, [navigate, location]);

  return null;
};


const RegionLayout = () => (
  <RegionProvider>
    <SeoHead />
    <Outlet />
  </RegionProvider>
);

const lazyPage = (loader: () => Promise<{ default: ComponentType }>) => async () => {
  const module = await loader();
  return { Component: module.default };
};

const regionChildren: RouteObject[] = [
  { index: true, lazy: lazyPage(() => import("@/pages/Index")) },
  { path: "about", lazy: lazyPage(() => import("@/pages/About")) },
  { path: "abm", lazy: lazyPage(() => import("@/pages/ABM")) },
  {
    path: "corporate-influencer",
    lazy: lazyPage(() => import("@/pages/CorporateInfluencer")),
  },
  { path: "linkedin-ads", lazy: lazyPage(() => import("@/pages/LinkedInAds")) },
  {
    path: "personal-branding",
    lazy: lazyPage(() => import("@/pages/PersonalBranding")),
  },
  { path: "hubspot-revops", lazy: lazyPage(() => import("@/pages/HubSpotRevOps")) },
  { path: "telemarketing", lazy: lazyPage(() => import("@/pages/Telemarketing")) },
  { path: "mail-kampagne", lazy: lazyPage(() => import("@/pages/MailKampagne")) },
  // /en/mail-campaigns ist die englische URL; der jeweils andere Slug leitet
  // auf Netlify per 301 weiter, clientseitig rendern beide die Seite.
  { path: "mail-campaigns", lazy: lazyPage(() => import("@/pages/MailKampagne")) },
  { path: "motion-ai", lazy: lazyPage(() => import("@/pages/MotionAI")) },
  { path: "n8n-automations", lazy: lazyPage(() => import("@/pages/N8NAutomations")) },
  { path: "dm-outbound", lazy: lazyPage(() => import("@/pages/DMOutbound")) },
  { path: "ip-tracking", lazy: lazyPage(() => import("@/pages/IPTracking")) },
  { path: "einstiegsangebot", lazy: lazyPage(() => import("@/pages/Einstiegsangebot")) },
  {
    path: "einstiegsangebot/welcome",
    lazy: lazyPage(() => import("@/pages/EinstiegsangebotWelcome")),
  },
  { path: "booking", lazy: lazyPage(() => import("@/pages/Booking")) },
  {
    path: "meeting-confirmed",
    lazy: lazyPage(() => import("@/pages/MeetingConfirmed")),
  },
  {
    path: "meetingconfirmed",
    lazy: lazyPage(() => import("@/pages/MeetingConfirmedExternal")),
  },
  { path: "webinare", lazy: lazyPage(() => import("@/pages/Webinare")) },
  {
    path: "webinar-confirmed",
    lazy: lazyPage(() => import("@/pages/WebinarConfirmed")),
  },
  { path: "impressum", lazy: lazyPage(() => import("@/pages/Impressum")) },
  { path: "datenschutz", lazy: lazyPage(() => import("@/pages/Datenschutz")) },
  { path: "revenue-blueprint", lazy: lazyPage(() => import("@/pages/RevenueBlueprintPage")) },
  { path: "resources", lazy: lazyPage(() => import("@/pages/Resources")) },
  { path: "blog", lazy: lazyPage(() => import("@/pages/Blog")) },
  { path: "blog/:slug", lazy: lazyPage(() => import("@/pages/BlogPost")) },
  { path: "case-studies", lazy: lazyPage(() => import("@/pages/CaseStudies")) },
  { path: "case-studies/bs-group", lazy: lazyPage(() => import("@/pages/CaseStudyBSGroup")) },
  { path: "case-studies/nexenio", lazy: lazyPage(() => import("@/pages/CaseStudyNeXenio")) },
  { path: "case-studies/saas-crm-plattform", lazy: lazyPage(() => import("@/pages/CaseStudySaasCrmPlattform")) },
  { path: "case-studies/cloud7", lazy: lazyPage(() => import("@/pages/CaseStudyCloud7")) },
  { path: "partnerbestellungen", lazy: lazyPage(() => import("@/pages/PartnerOrders")) },
  { path: "partnerbestellungen/:productSlug", lazy: lazyPage(() => import("@/pages/PartnerOrderDetail")) },
  { path: "*", lazy: lazyPage(() => import("@/pages/NotFound")) },
];

export const routes: RouteRecord[] = [
  {
    path: "/",
    Component: App,
    children: [
      { index: true, Component: RootRedirect },
      {
        path: ":lang",
        Component: RegionLayout,
        // Make all localized paths prerenderable for SSG.
        getStaticPaths: () => REGION_CODES,
        children: regionChildren,
      },
      { path: "*", Component: UnprefixedRedirect },
    ],
  },
];
